import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import type {
  FilterPersistenceContextType,
  FilterPersistenceState,
  FilterState,
  CountsState,
  ScreenState,
} from './types';

const FilterPersistenceContext = createContext<FilterPersistenceContextType | undefined>(undefined);

export const FILTER_PERSISTENCE_STORAGE_KEY = 'filter-persistence-state';
const STORAGE_KEY = FILTER_PERSISTENCE_STORAGE_KEY;

function getInitialState(): FilterPersistenceState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Filter persistence: failed to restore state from sessionStorage', error);
  }
  return {};
}

interface FilterPersistenceProviderProps {
  children: React.ReactNode;
}

export function FilterPersistenceProvider({ children }: FilterPersistenceProviderProps) {
  const [persistenceState, setPersistenceState] = useState<FilterPersistenceState>(getInitialState);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persistenceState));
    } catch (error) {
      console.warn('Filter persistence: failed to save state to sessionStorage', error);
    }
  }, [persistenceState]);

  const getFilters = useCallback((screenKey: string): FilterState | undefined => {
    return persistenceState[screenKey]?.filters;
  }, [persistenceState]);

  const setFilters = useCallback((screenKey: string, filters: Partial<FilterState>) => {
    setPersistenceState((prev) => ({
      ...prev,
      [screenKey]: {
        ...prev[screenKey],
        filters: {
          ...prev[screenKey]?.filters,
          ...filters,
          lastUpdated: Date.now(),
        } as FilterState,
        lastUpdated: Date.now(),
      },
    }));
  }, []);

  const getCounts = useCallback((screenKey: string): CountsState | undefined => {
    return persistenceState[screenKey]?.counts;
  }, [persistenceState]);

  const setCounts = useCallback((screenKey: string, counts: CountsState) => {
    setPersistenceState((prev) => ({
      ...prev,
      [screenKey]: {
        ...prev[screenKey],
        counts: {
          ...prev[screenKey]?.counts,
          ...counts,
        },
        lastUpdated: Date.now(),
      },
    }));
  }, []);

  const getScreenState = useCallback((screenKey: string): ScreenState | undefined => {
    return persistenceState[screenKey];
  }, [persistenceState]);

  const setScreenState = useCallback((screenKey: string, state: Partial<ScreenState>) => {
    setPersistenceState((prev) => ({
      ...prev,
      [screenKey]: {
        ...prev[screenKey],
        ...state,
        lastUpdated: Date.now(),
      },
    }));
  }, []);

  const clearFilters = useCallback((screenKey: string) => {
    setPersistenceState((prev) => {
      const newState = { ...prev };
      delete newState[screenKey];
      return newState;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setPersistenceState({});
  }, []);

  const clearFiltersForNetwork = useCallback((networkId: string) => {
    const prefix = `network-${networkId}`;
    setPersistenceState((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key.includes(prefix)) delete newState[key];
      });
      return newState;
    });
  }, []);

  const contextValue = useMemo<FilterPersistenceContextType>(() => ({
    getFilters,
    setFilters,
    getCounts,
    setCounts,
    getScreenState,
    setScreenState,
    clearFilters,
    clearAllFilters,
    clearFiltersForNetwork,
  }), [
    getFilters, setFilters, getCounts, setCounts, getScreenState,
    setScreenState, clearFilters, clearAllFilters, clearFiltersForNetwork]);

  return (
    <FilterPersistenceContext.Provider value={contextValue}>
      {children}
    </FilterPersistenceContext.Provider>
  );
}

export function useFilterPersistenceContext(): FilterPersistenceContextType {
  const context = useContext(FilterPersistenceContext);
  if (context === undefined) {
    throw new Error('useFilterPersistenceContext must be used within a FilterPersistenceProvider');
  }
  return context;
}

export function useFilterPersistenceContextOptional(): FilterPersistenceContextType | undefined {
  return useContext(FilterPersistenceContext);
}
